import { useEffect, useRef, useState, type ReactNode } from "react"
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { ClockAction, ClockTipInfo } from "../../lib/clock"

const OPEN_DELAY_MS = 150
const CLOSE_DELAY_MS = 120

/**
 * A clock part that explains itself: hover (after 150 ms), keyboard focus or a tap opens the tooltip;
 * moving away, Esc or a tap elsewhere closes it. The trigger is a real button (focusable, at least
 * 32x32 px hit area) whose accessible name is the tooltip's title and body. With `onAction` the
 * tooltip carries one action button (the case panel only).
 */
export function ClockTip({
  info,
  onAction,
  className,
  style,
  tabIndex,
  children,
}: {
  info: ClockTipInfo
  onAction?: (mode: ClockAction) => void
  className?: string
  style?: React.CSSProperties
  tabIndex?: number
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const timer = useRef<number | undefined>(undefined)
  const clear = () => window.clearTimeout(timer.current)
  const later = (next: boolean, ms: number) => {
    clear()
    timer.current = window.setTimeout(() => setOpen(next), ms)
  }
  useEffect(() => clear, [])
  const action = onAction && info.action

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <button
          type="button"
          tabIndex={tabIndex}
          aria-label={`${info.title}. ${info.body}`}
          aria-expanded={open}
          className={cn(
            "absolute flex items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring",
            className,
          )}
          style={style}
          onPointerEnter={(e) => e.pointerType === "mouse" && later(true, OPEN_DELAY_MS)}
          onPointerLeave={(e) => e.pointerType === "mouse" && later(false, CLOSE_DELAY_MS)}
          onFocus={() => {
            clear()
            setOpen(true)
          }}
          onBlur={(e) => {
            if (!e.relatedTarget || !(e.relatedTarget as HTMLElement).closest("[data-clock-tip]")) later(false, 0)
          }}
          onClick={(e) => {
            e.stopPropagation() // a row click would open the case
            clear()
            setOpen((o) => !o)
          }}
        >
          {children}
        </button>
      </PopoverAnchor>
      {/* pointer-events-auto: in the case panel (a modal sheet) the body has pointer-events: none. */}
      <PopoverContent
        data-clock-tip
        side="top"
        className="pointer-events-auto w-64 p-3 text-xs motion-reduce:animate-none"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onPointerEnter={clear}
        onPointerLeave={(e) => e.pointerType === "mouse" && later(false, CLOSE_DELAY_MS)}
        onClick={(e) => e.stopPropagation()}
      >
        <p className="font-semibold text-[13px] text-foreground">{info.title}</p>
        <p className="mt-1 leading-relaxed text-muted-foreground">{info.body}</p>
        {action && (
          <Button
            size="sm"
            className="mt-2.5 h-7 text-xs"
            onClick={() => {
              setOpen(false)
              onAction(action.mode)
            }}
          >
            {action.label}
          </Button>
        )}
      </PopoverContent>
    </Popover>
  )
}
